import { SORTING_TYPES, type SortingTypeWithoutNoSorting } from '@pequity/shared/ui';
import { toString } from '@pequity/shared/utils';
import { cloneDeep, isEmpty } from 'lodash-es';
import type { Col, FilterState, QueryObj, QueryObjFilters } from '@/ccpv2/types';

/**
 * The default filter state.
 *
 * @type {Object}
 */
export const createEmptyFilterState = (): FilterState => {
  return {
    selectedOptions: [],
    sorting: SORTING_TYPES.NO_SORTING,
    min: null,
    max: null,
  } satisfies FilterState;
};

export const setFilterOrder = (colId: number, filters: QueryObjFilters | Record<string, never>) => {
  const res = cloneDeep(filters);
  res.order = res.order || [];

  const index = res.order.findIndex((id) => colId === id);
  const filtersAreEmpty = isEmpty(filters?.[colId] || []);

  if (index === -1 && !filtersAreEmpty) {
    res.order.push(colId);
  } else if (index !== -1 && filtersAreEmpty) {
    res.order.splice(index, 1);
  }

  return res;
};

export const setFilterStateInCols = <T extends Pick<Col, 'id' | 'filterState' | 'filterActive'>>(
  cols: T[],
  queryFilters: QueryObj['filters'],
  queryOrdering: QueryObj['ordering']
) => {
  return cloneDeep(cols).map((col) => {
    const colId = toString(col.id);

    // Set Filtering
    col.filterState = queryFilters[colId]
      ? { ...createEmptyFilterState(), ...queryFilters[colId] }
      : createEmptyFilterState();
    queryFilters = setFilterOrder(Number(colId), queryFilters);

    // Set sorting
    if (queryOrdering.includes(`${col.id}`)) {
      col.filterState.sorting = SORTING_TYPES.ASC;
    }
    if (queryOrdering.includes(`-${col.id}`)) {
      col.filterState.sorting = SORTING_TYPES.DESC;
    }

    // Set filterActive
    col.filterActive = Boolean(
      col.filterState.selectedOptions.length ||
      [SORTING_TYPES.ASC, SORTING_TYPES.DESC].includes(col.filterState.sorting as SortingTypeWithoutNoSorting) ||
      col.filterState.min ||
      col.filterState.max
    );

    return col;
  });
};
